import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import DomainAddRoundedIcon from '@mui/icons-material/DomainAddRounded'
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded'
import Autocomplete from '@mui/material/Autocomplete'
import { blueGrey } from '@mui/material/colors'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/system/Box'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetCustomersQuery } from '@/app/services/customers'
import { useUpdateQuoteMutation } from '@/app/services/quotes'
import { selectAllContacts, setSelectedContact } from '@/app/slices/contactsSlice'
import { selectCustomers, setSelectedCustomer, setSelectedCustomerId } from '@/app/slices/customersSlice'
import { selectDefaultTaxRateId, selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxDrawer from '@/common/components/TbxDrawer/TbxDrawer'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'
import AddContactForm from '@/features/customer-central/components/AddContactForm'
import AddCustomerForm from '@/features/customer-central/components/AddCustomerForm'

const classes = {
    disabledInput: {
        '& .Mui-disabled': {
            textFillColor: '#34495E',
        },
        '& .MuiInput-input.Mui-disabled': {
            textFillColor: '#34495E',
            fontWeight: 'bold',
        },
    },
}

const QuoteCustomerContact = ({ hasPartsFromLibrary = false, quote, refetch }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)

    useGetCustomersQuery({ organisationId, params: { includeWebStore: true } })
    const [updateQuote] = useUpdateQuoteMutation()

    const allCustomers = useSelector(selectCustomers)
    const allContacts = useSelector(selectAllContacts)
    const defaultTaxRateId = useSelector(selectDefaultTaxRateId)

    const [localSelectedCustomer, setLocalSelectedCustomer] = useState(null)
    const [localSelectedContact, setLocalSelectedContact] = useState(null)

    const contacts = useMemo(() => {
        if (!localSelectedCustomer?.id) {
            return allContacts
        }
        const selectedCustomer = allCustomers.find(({ customerId }) => customerId === localSelectedCustomer.id)
        return selectedCustomer?.contacts ?? []
    }, [allContacts, localSelectedCustomer, allCustomers])

    const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false)
    const [showAddContactDialog, setShowAddContactDialog] = useState(false)

    const isInputDisabled = useMemo(() => {
        return ![QUOTE_STATUS_ARRAY.NotCalculated, QUOTE_STATUS_ARRAY.Calculated, QUOTE_STATUS_ARRAY.Draft].includes(
            quote?.status
        )
    }, [quote?.status])

    const updateQuoteCustomerContact = useCallback(
        async ({
            changeShippingOption = true,
            changeStatus = false,
            contactId,
            customerId,
            customerName,
            taxRateId,
            updateAddresses = true,
        }) => {
            try {
                const { billingAddress, deliveryAddress, ...quoteWithoutAddresses } = quote
                const customer = allCustomers.find(({ customerId: id }) => id === customerId)
                const customerBillingAddress = customer?.billingAddress || null
                await updateQuote({
                    organisationId,
                    quoteId: quote.id,
                    quote: {
                        ...quoteWithoutAddresses,
                        customerContactId: contactId ?? '',
                        customerId: customerId ?? '',
                        customerName: customerName ?? '',
                        taxRateId: taxRateId ?? defaultTaxRateId,
                        ...(changeShippingOption && { deliveryOption: 'Pickup' }),
                        ...(updateAddresses && {
                            billingAddressId: customerBillingAddress?.addressId ?? null,
                            billingAddress: customerBillingAddress,
                            deliveryAddressId: null,
                            deliveryAddress: null,
                        }),
                        ...(changeStatus && { status: QUOTE_STATUS_ARRAY.NotCalculated }),
                    },
                }).unwrap()
            } catch {
                enqueueSnackbar(t('An error occurred updating the $t(quote) contact'), {
                    variant: 'error',
                })
            }
        },
        [organisationId, quote, allCustomers, defaultTaxRateId, enqueueSnackbar, t, updateQuote]
    )

    const handleCustomerChange = useCallback(
        (_event, customerOption) => {
            if (!customerOption || customerOption?.id === localSelectedCustomer?.id) return

            if (customerOption.id === 'newCustomer') {
                updateQuoteCustomerContact({
                    contactId: null,
                    customerId: null,
                    customerName: null,
                    taxRateId: null,
                    changeStatus: true,
                })
                setShowAddCustomerDialog(true)
                return
            }

            const selectedCustomer = allCustomers.find(({ customerId }) => customerId === customerOption.id)
            const selectedContacts = selectedCustomer?.contacts?.filter((contact) => !contact.isDeleted) ?? []
            const defaultContact =
                selectedContacts?.find((contact) => contact.isDefault === true) || selectedContacts[0]

            setLocalSelectedCustomer(customerOption)
            dispatch(setSelectedCustomer(selectedCustomer))
            dispatch(setSelectedCustomerId(selectedCustomer?.customerId))
            dispatch(setSelectedContact(defaultContact))

            setLocalSelectedContact({
                id: defaultContact?.contactId ?? '',
                label: defaultContact?.name ?? '',
                customerId: selectedCustomer?.customerId ?? '',
            })

            if (defaultContact) {
                updateQuoteCustomerContact({
                    contactId: defaultContact.contactId,
                    customerId: selectedCustomer.customerId,
                    customerName: selectedCustomer.companyName,
                    taxRateId: selectedCustomer.taxRateId,
                    changeStatus: true,
                })
            }
        },
        [localSelectedCustomer?.id, updateQuoteCustomerContact, allCustomers, dispatch]
    )

    const handleContactChange = useCallback(
        (_event, contactOption) => {
            if (!contactOption || contactOption.id === localSelectedContact?.id) return

            if (contactOption.id === 'newContact') {
                setShowAddContactDialog(true)
                return
            }

            const customer = allCustomers.find(({ customerId }) => customerId === contactOption?.customerId)
            const selectedContacts = customer?.contacts ?? []
            const selectedContact = selectedContacts?.find((contact) => contact.contactId === contactOption.id)

            if (!localSelectedCustomer?.id) {
                setLocalSelectedCustomer({
                    id: customer.customerId,
                    label: customer.companyName,
                })

                dispatch(setSelectedCustomer(contactOption))
            }
            setLocalSelectedContact(contactOption)
            dispatch(setSelectedContact(selectedContact))

            updateQuoteCustomerContact({
                contactId: contactOption.id,
                customerId: contactOption.customerId,
                customerName: customer.companyName,
                taxRateId: customer.taxRateId,
                updateAddresses: false,
                changeShippingOption: false,
            })
        },
        [localSelectedContact?.id, allCustomers, localSelectedCustomer?.id, dispatch, updateQuoteCustomerContact]
    )

    const handleAddCustomer = useCallback(
        async (newCustomer) => {
            const customerOption = {
                id: newCustomer.customerId,
                label: newCustomer.companyName,
            }

            // Dispatch Redux actions for quote context (previously done in AddCustomerForm)
            dispatch(setSelectedCustomerId(newCustomer.customerId))
            dispatch(setSelectedCustomer(newCustomer))

            setLocalSelectedCustomer(customerOption)
            setShowAddCustomerDialog(false)
            setShowAddContactDialog(true)
        },
        [dispatch]
    )

    const handleAddContact = useCallback(
        async (newContact) => {
            const contactOption = {
                id: newContact.contactId,
                label: newContact.name,
                customerId: newContact.customerId,
            }
            handleContactChange(null, contactOption)
            setShowAddContactDialog(false)
        },
        [handleContactChange]
    )

    const handleCloseAddCustomerDialog = useCallback(() => {
        refetch()
        setShowAddCustomerDialog(false)
    }, [refetch])

    const handleCloseAddContactDialog = useCallback(() => {
        handleContactChange(null, null)
        setShowAddContactDialog(false)
    }, [handleContactChange])

    const getOptionLabelCustomer = (option) => option?.label ?? ''

    const getOptionLabelContact = (option) => option?.label ?? ''

    const getOptionSelectedCustomer = (option, value) => option.id === value.id

    const getOptionSelectedContact = (option, value) => option.id === value.id

    const customersOptions = useMemo(() => {
        const customersList = allCustomers
            .filter((customer) => !customer.isDeleted)
            .map((customer) => {
                return {
                    id: customer.customerId,
                    label: customer.companyName,
                }
            })
        customersList.unshift({
            id: 'newCustomer',
            label: t('Add Customer'),
        })
        return customersList
    }, [allCustomers, t])

    const contactsOptions = useMemo(() => {
        const unarchivedCustomerIds = allCustomers
            ?.filter((customer) => !customer.isDeleted)
            .map((customer) => customer.customerId)

        const contactsList = contacts
            .filter((contact) => !contact.isDeleted && unarchivedCustomerIds.includes(contact.customerId))
            .map((contact) => {
                return {
                    id: contact.contactId,
                    label: contact.name,
                    customerId: contact.customerId,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))

        contactsList.unshift({
            id: 'newContact',
            label: t('Add Contact'),
            customerId: localSelectedCustomer?.id ?? '',
        })

        return contactsList
    }, [contacts, allCustomers, localSelectedCustomer, t])

    const savedCustomerContact = useMemo(() => {
        if (quote?.customerContactId) {
            const selectedContact = allContacts.find(({ contactId }) => contactId === quote.customerContactId)
            const selectedCustomer = allCustomers.find(({ customerId }) => customerId === selectedContact?.customerId)

            const savedCustomer = {
                id: selectedCustomer?.customerId ?? '',
                label: selectedCustomer?.companyName ?? '',
            }
            const savedContact = {
                id: selectedContact?.contactId ?? '',
                label: selectedContact?.name ?? '',
                customerId: selectedCustomer?.customerId ?? '',
            }

            return { savedCustomer, savedContact, selectedCustomer, selectedContact }
        }
    }, [quote, allContacts, allCustomers])

    const renderOption = (props, option) => {
        return (
            <Box
                component="li"
                {...props}
                sx={[
                    (option.id === 'newCustomer' || option.id === 'newContact') && {
                        borderBottom: `1px solid ${blueGrey[100]}`,
                    },
                ]}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: 1,
                    }}
                >
                    <Typography>{option.label}</Typography>
                    {(option.id === 'newCustomer' && <DomainAddRoundedIcon fontSize="small" />) ||
                        (option.id === 'newContact' && <PersonAddRoundedIcon fontSize="small" />)}
                </Box>
            </Box>
        )
    }

    // Sync local state from quote when customerContactId changes
    // Skip reset when in the middle of adding a customer/contact flow

    useEffect(() => {
        // Don't reset state if we're in the middle of adding a customer or contact
        if (showAddCustomerDialog || showAddContactDialog) {
            return
        }

        if (!quote?.customerContactId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setLocalSelectedCustomer({ id: '', label: '' })
            setLocalSelectedContact({ id: '', label: '', customerId: '' })
            dispatch(setSelectedCustomer(undefined))
            dispatch(setSelectedCustomerId(undefined))
            dispatch(setSelectedContact(undefined))
            return
        }

        const selectedContact = allContacts.find(({ contactId }) => contactId === quote.customerContactId)
        const selectedCustomer = allCustomers.find(({ customerId }) => customerId === selectedContact?.customerId)

        setLocalSelectedCustomer({
            id: selectedCustomer?.customerId ?? '',
            label: selectedCustomer?.companyName ?? '',
        })
        setLocalSelectedContact({
            id: selectedContact?.contactId ?? '',
            label: selectedContact?.name ?? '',
            customerId: selectedCustomer?.customerId ?? '',
        })

        dispatch(setSelectedCustomer(selectedCustomer))
        dispatch(setSelectedCustomerId(selectedCustomer?.customerId))
        dispatch(setSelectedContact(selectedContact))
    }, [quote?.customerContactId, allContacts, allCustomers, dispatch, showAddCustomerDialog, showAddContactDialog])

    return (
        <>
            <Box
                sx={{
                    alignItems: 'flex-start',
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    gap: 2,
                    justifyContent: 'space-between',
                    width: 240,
                }}
            >
                <TbxTooltip
                    key="customer-tooltip"
                    title={
                        hasPartsFromLibrary
                            ? t(
                                  "This quote has parts from this customer's Part Library, so the customer cannot be changed until those parts are removed from the quote"
                              )
                            : ''
                    }
                    arrow
                >
                    <Autocomplete
                        disabled={isInputDisabled || hasPartsFromLibrary}
                        forcePopupIcon={!isInputDisabled}
                        getOptionKey={(option) => option.id}
                        getOptionLabel={getOptionLabelCustomer}
                        isOptionEqualToValue={getOptionSelectedCustomer}
                        options={customersOptions}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                color="secondary"
                                label={!localSelectedCustomer?.id ? t('Select customer') : t('Customer')}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        disableUnderline: isInputDisabled,
                                    },

                                    htmlInput: {
                                        ...params.inputProps,
                                        'data-testid': 'quote-customer-input',
                                    },

                                    inputLabel: {
                                        ...params.InputLabelProps,
                                        shrink: true,
                                    },
                                }}
                            />
                        )}
                        renderOption={renderOption}
                        sx={classes.disabledInput}
                        value={localSelectedCustomer ?? null}
                        disableClearable
                        fullWidth
                        handleHomeEndKeys
                        selectOnFocus
                        onChange={handleCustomerChange}
                    />
                </TbxTooltip>

                <Autocomplete
                    disabled={isInputDisabled}
                    forcePopupIcon={!isInputDisabled}
                    getOptionDisabled={(option) => !option.customerId}
                    getOptionKey={(option) => option.id}
                    getOptionLabel={getOptionLabelContact}
                    isOptionEqualToValue={getOptionSelectedContact}
                    options={contactsOptions}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            color="secondary"
                            label={!localSelectedContact?.id ? t('Select contact') : t('Contact')}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    disableUnderline: isInputDisabled,
                                },

                                htmlInput: {
                                    ...params.inputProps,
                                    'data-testid': 'quote-contact-input',
                                },

                                inputLabel: {
                                    ...params.InputLabelProps,
                                    shrink: true,
                                },
                            }}
                        />
                    )}
                    renderOption={renderOption}
                    sx={classes.disabledInput}
                    value={localSelectedContact ?? savedCustomerContact?.savedContact ?? null}
                    disableClearable
                    fullWidth
                    handleHomeEndKeys
                    selectOnFocus
                    onChange={handleContactChange}
                />
            </Box>
            <TbxDrawer
                showDrawer={showAddCustomerDialog}
                onClose={handleCloseAddCustomerDialog}
            >
                <AddCustomerForm
                    onCancel={handleCloseAddCustomerDialog}
                    onCreate={handleAddCustomer}
                />
            </TbxDrawer>
            <TbxDrawer
                showDrawer={showAddContactDialog}
                onClose={handleCloseAddContactDialog}
            >
                <AddContactForm
                    contact={null}
                    customerId={localSelectedCustomer?.id}
                    onCancel={handleCloseAddContactDialog}
                    onCreate={handleAddContact}
                />
            </TbxDrawer>
        </>
    )
}

QuoteCustomerContact.propTypes = {
    quote: PropTypes.object.isRequired,
    refetch: PropTypes.func.isRequired,
    hasPartsFromLibrary: PropTypes.bool,
}

export default QuoteCustomerContact
